import React, { useEffect, useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Plus, Trash2, Upload, Music, Radio, Save, BarChart3, Calendar, X, Code2, Copy, Check, Mic2, Sparkles, RefreshCw, FileText } from "lucide-react";
import ReferralCard from "@/components/ReferralCard";
import StationPulseUpsell from "@/components/StationPulseUpsell";
import DemographicsPanel from "@/components/DemographicsPanel";
import TrackArtworkButton from "@/components/TrackArtworkButton";

const DEFAULT_NEW = { name: "", tagline: "", genre: "mixed", language: "english", country: "GLOBAL", age_target: "all", cover_url: "", tip_jar_url: "", zip_code: "" };
const DAYS = ["Mon","Tue","Wed","Thu","Fri","Sat","Sun"];

function authHeaders() {
  const t = localStorage.getItem("fliboe_token");
  return t ? { Authorization: `Bearer ${t}` } : {};
}

export default function Dashboard() {
  const { user, loading } = useAuth();
  const [myStations, setMyStations] = useState([]);
  const [selected, setSelected] = useState(null);
  const [stationData, setStationData] = useState(null);
  const [catalog, setCatalog] = useState({ genres: [], countries: [], languages: [], age_targets: [] });
  const [showCreate, setShowCreate] = useState(false);
  const [newSt, setNewSt] = useState(DEFAULT_NEW);
  const [analytics, setAnalytics] = useState(null);
  const [agreementChecked, setAgreementChecked] = useState(false);
  const [showAgreement, setShowAgreement] = useState(false);
  const [tab, setTab] = useState("playlist");
  const [showAddShow, setShowAddShow] = useState(false);
  const [newShow, setNewShow] = useState({ title: "", host: "", day_of_week: 0, start_hour: 18, duration_min: 60, description: "" });
  const fileRef = useRef(null);
  const [uploadBusy, setUploadBusy] = useState(false);
  const [showAddByUrl, setShowAddByUrl] = useState(false);
  const [editingLyricsTrack, setEditingLyricsTrack] = useState(null);
  const [lyricsDraft, setLyricsDraft] = useState("");
  const [savingLyrics, setSavingLyrics] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  useEffect(() => { setLyricsDraft(editingLyricsTrack?.lyrics || ""); }, [editingLyricsTrack?.track_id]);
  const saveLyrics = async () => {
    if (!editingLyricsTrack) return;
    setSavingLyrics(true);
    try {
      await api.patch(`/tracks/${editingLyricsTrack.track_id}`, { lyrics: lyricsDraft }, { headers: authHeaders() });
      const r = await api.get(`/stations/${selected}`); setStationData(r.data);
      setEditingLyricsTrack(null);
    } catch (e) {
      alert(e?.response?.data?.detail || "Could not save lyrics.");
    }
    finally { setSavingLyrics(false); }
  };
  const transcribeLyrics = async () => {
    if (!editingLyricsTrack) return;
    if (!window.confirm("Run AI transcription on this track? Costs ~$0.006 per minute of audio (Emergent credits). The result will REPLACE the current lyrics textbox.")) return;
    setTranscribing(true);
    try {
      const { data } = await api.post(`/tracks/${editingLyricsTrack.track_id}/transcribe`, {}, { headers: authHeaders(), timeout: 180000 });
      if (data.lyrics) {
        setLyricsDraft(data.lyrics);
      } else {
        alert(data.message || "Whisper returned empty — likely an instrumental.");
      }
    } catch (e) {
      alert(e?.response?.data?.detail || "Transcription failed. Try again or paste lyrics manually.");
    }
    finally { setTranscribing(false); }
  };
  const [health, setHealth] = useState(null);

  useEffect(() => {
    (async () => {
      const c = await api.get("/catalog"); setCatalog(c.data);
    })();
  }, []);

  const loadMine = async () => {
    try {
      const { data } = await api.get("/stations/mine/list", { headers: authHeaders() });
      setMyStations(data);
      if (data.length && !selected) setSelected(data[0].station_id);
    } catch (_) {}
  };

  useEffect(() => { if (user) loadMine(); }, [user]);

  useEffect(() => {
    if (!selected) { setStationData(null); setHealth(null); return; }
    (async () => {
      const r = await api.get(`/stations/${selected}`);
      setStationData(r.data);
      try {
        const a = await api.get(`/stations/${selected}/analytics`, { headers: authHeaders() });
        setAnalytics(a.data);
      } catch (_) {}
      try {
        const h = await api.get(`/stations/${selected}/health`, { headers: authHeaders() });
        setHealth(h.data);
      } catch (_) { setHealth(null); }
    })();
  }, [selected]);

  if (loading) return <div className="max-w-7xl mx-auto px-5 py-20 text-slate-500 fl-mono text-xs uppercase tracking-widest">// Authenticating…</div>;
  if (!user) {
    return (
      <div className="max-w-md mx-auto px-5 py-32 text-center">
        <Radio className="w-12 h-12 text-emerald-500 mx-auto" />
        <h1 className="fl-display text-3xl font-bold tracking-tight mt-4">Sign in to your studio</h1>
        <p className="mt-3 text-slate-400">Broadcasters create and manage stations from here.</p>
        <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
          <Link to="/login" className="fl-btn ghost" data-testid="dashboard-login-link">Sign in</Link>
          <Link to="/signup" className="fl-btn" data-testid="dashboard-signup-link">Create account</Link>
        </div>
      </div>
    );
  }

  const createStation = async () => {
    if (!newSt.name) return;
    const { data } = await api.post("/stations", newSt, { headers: authHeaders() });
    setShowCreate(false); setNewSt(DEFAULT_NEW);
    await loadMine();
    setSelected(data.station_id);
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selected) return;
    setUploadBusy(true);
    try {
      // probe duration via Audio element
      const url = URL.createObjectURL(file);
      const duration = await new Promise((resolve) => {
        const a = new Audio();
        a.src = url;
        a.addEventListener("loadedmetadata", () => resolve(a.duration || 0));
        a.addEventListener("error", () => resolve(0));
      });
      URL.revokeObjectURL(url);
      const fd = new FormData();
      fd.append("file", file);
      fd.append("title", file.name.replace(/\.[^.]+$/, ""));
      fd.append("artist", user.name);
      fd.append("duration", String(duration || 180));
      fd.append("kind", "music");
      await api.post(`/stations/${selected}/upload`, fd, { headers: { ...authHeaders(), "Content-Type": "multipart/form-data" } });
      const r = await api.get(`/stations/${selected}`); setStationData(r.data);
    } finally {
      setUploadBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const addExternal = () => setShowAddByUrl(true);

  const reloadStation = async () => {
    const r = await api.get(`/stations/${selected}`);
    setStationData(r.data);
  };

  const deleteTrack = async (tid) => {
    await api.delete(`/tracks/${tid}`, { headers: authHeaders() });
    const r = await api.get(`/stations/${selected}`); setStationData(r.data);
  };

  const saveSettings = async (patch) => {
    await api.patch(`/stations/${selected}`, patch, { headers: authHeaders() });
    await loadMine();
    const r = await api.get(`/stations/${selected}`); setStationData(r.data);
  };

  const addShow = async () => {
    if (!newShow.title || !newShow.host) return;
    await api.post(`/stations/${selected}/shows`, newShow, { headers: authHeaders() });
    setShowAddShow(false);
    setNewShow({ title: "", host: "", day_of_week: 0, start_hour: 18, duration_min: 60, description: "" });
    const r = await api.get(`/stations/${selected}`); setStationData(r.data);
  };

  const deleteShow = async (id) => {
    await api.delete(`/shows/${id}`, { headers: authHeaders() });
    const r = await api.get(`/stations/${selected}`); setStationData(r.data);
  };

  const s = stationData?.station;
  const tracks = stationData?.tracks || [];
  const shows = stationData?.shows || [];

  return (
    <main className="max-w-7xl mx-auto px-5 py-10 pb-40">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
        <div>
          <div className="fl-mono text-[10px] tracking-[0.3em] uppercase text-emerald-500 mb-1">// Studio</div>
          <h1 className="fl-display text-4xl sm:text-5xl font-extrabold tracking-tighter">CONTROL ROOM</h1>
          <p className="text-slate-400 mt-1">Welcome back, {user.name}. Manage your broadcast.</p>
        </div>
        <button onClick={() => setShowCreate(true)} className="fl-btn" data-testid="create-station-button">
          <Plus className="w-4 h-4" /> New station
        </button>
      </div>

      <ReferralCard />

      {myStations.length === 0 ? (
        <div className="fl-card p-10 text-center">
          <Radio className="w-10 h-10 text-emerald-500 mx-auto" />
          <h2 className="fl-display text-2xl font-bold mt-4">Spin up your first station</h2>
          <p className="text-slate-400 mt-2">Name it, tag your audience, and you're broadcasting in seconds.</p>
          <button onClick={() => setShowCreate(true)} className="fl-btn mt-6" data-testid="empty-create-button">Create station</button>
        </div>
      ) : (
        <div className="grid lg:grid-cols-12 gap-6">
          {/* Stations list */}
          <aside className="lg:col-span-3 space-y-2">
            <div className="fl-mono text-[10px] tracking-[0.25em] uppercase text-slate-500 mb-2">// My stations</div>
            {myStations.map(st => (
              <button
                key={st.station_id}
                onClick={() => setSelected(st.station_id)}
                className={`w-full text-left p-3 rounded-lg border transition ${selected === st.station_id ? "border-emerald-500/60 bg-emerald-500/5" : "border-white/5 bg-[#121212] hover:border-white/15"}`}
                data-testid={`select-station-${st.station_id}`}
              >
                <div className="text-sm font-semibold truncate">{st.name}</div>
                <div className="fl-mono text-[10px] uppercase tracking-widest text-slate-500 mt-1">{st.genre} · {st.country}</div>
              </button>
            ))}
          </aside>

          {/* Selected station editor */}
          <section className="lg:col-span-9 space-y-6">
            {s && (
              <>
                {/* Header card with quick stats */}
                <div className="fl-card p-6">
                  <div className="flex items-center justify-between flex-wrap gap-4">
                    <div className="min-w-0">
                      <div className="fl-mono text-[10px] tracking-[0.3em] uppercase text-emerald-500 flex items-center gap-2"><span className="live-dot" /> On Air</div>
                      <h2 className="fl-display text-3xl font-bold tracking-tighter mt-1 truncate">{s.name}</h2>
                      <div className="text-xs text-slate-400 mt-1">{s.tagline || "—"}</div>
                    </div>
                    <div className="flex items-center gap-6">
                      <Stat label="Listeners" value={analytics?.current_listeners ?? 0} />
                      <Stat label="Tracks" value={tracks.length} />
                      <Stat label="Shows" value={shows.length} />
                    </div>
                  </div>
                </div>

                {/* Touchpoint A: One-click Pulse SEO upsell pre-filled from this station */}
                <StationPulseUpsell station={s} />

                {/* Tabs */}
                <div className="flex gap-2 flex-wrap">
                  {[
                    { k: "playlist", label: "Playlist", icon: Music },
                    { k: "shows", label: "Talk shows", icon: Calendar },
                    { k: "ai-dj", label: "AI DJ", icon: Mic2 },
                    { k: "settings", label: "Settings", icon: Save },
                    { k: "analytics", label: "Analytics", icon: BarChart3 },
                    { k: "embed", label: "Embed", icon: Code2 },
                  ].map(({ k, label, icon: Icon }) => (
                    <button key={k} onClick={() => setTab(k)} className={`fl-pill ${tab === k ? "is-active" : ""}`} data-testid={`tab-${k}`}>
                      <Icon className="w-3.5 h-3.5" /> {label}
                    </button>
                  ))}
                </div>

                {/* Broken-files banner (after a deploy that wiped local disk) */}
                {health && (health.tracks_broken > 0 || health.cover_ok === false) && (
                  <div className="fl-card p-4 border border-red-500/40 bg-red-500/5 flex items-start gap-3" data-testid="broken-files-banner">
                    <Trash2 className="w-5 h-5 text-red-400 shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-red-300">
                        {health.tracks_broken > 0 && `${health.tracks_broken} of ${health.tracks_total} track${health.tracks_total === 1 ? "" : "s"} are missing audio.`}
                        {health.cover_ok === false && (health.tracks_broken > 0 ? " Cover image is also missing." : "Your cover image is missing.")}
                      </div>
                      <div className="text-xs text-slate-300 mt-1">
                        Files uploaded before this update were stored on disk and got wiped by the platform's last deploy.
                        <strong className="text-emerald-400"> All new uploads from now on are stored in MongoDB and will survive every future deploy.</strong>
                      </div>
                      <div className="text-xs text-slate-400 mt-2">
                        <strong>Fix:</strong> {health.cover_ok === false && "go to Settings → upload a new cover. "}
                        {health.tracks_broken > 0 && "Delete the broken tracks below (Trash icon) and re-upload them via 'Upload MP3'."}
                      </div>
                    </div>
                  </div>
                )}

                {/* PLAYLIST TAB */}
                {tab === "playlist" && (
                  <div className="fl-card p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="fl-display text-xl font-bold">Tracks in rotation</h3>
                      <div className="flex gap-2">
                        <input ref={fileRef} type="file" accept="audio/*,.mp3,.wav,.m4a,.ogg,.aac,.flac" className="hidden" onChange={handleUpload} data-testid="upload-input" />
                        <button onClick={() => fileRef.current?.click()} disabled={uploadBusy} className="fl-btn !py-2 !px-3 text-sm" data-testid="upload-button">
                          <Upload className="w-4 h-4" /> {uploadBusy ? "Uploading…" : "Upload MP3"}
                        </button>
                        <button onClick={addExternal} className="fl-btn ghost !py-2 !px-3 text-sm" data-testid="add-external-button">
                          <Plus className="w-4 h-4" /> Add by URL
                        </button>
                      </div>
                    </div>
                    {tracks.length === 0 ? (
                      <div className="text-slate-500 fl-mono text-xs uppercase tracking-widest py-12 text-center">// Upload your first track</div>
                    ) : (
                      <div className="divide-y divide-white/5">
                        {tracks.map((t, i) => {
                          const tHealth = health?.tracks?.find(h => h.track_id === t.track_id);
                          const broken = tHealth && tHealth.ok === false;
                          return (
                          <div key={t.track_id} className={`py-3 flex items-center gap-3 ${broken ? "opacity-60" : ""}`}>
                            <span className="fl-mono text-xs text-slate-500 w-6">{String(i+1).padStart(2,"0")}</span>
                            <TrackArtworkButton
                              track={t}
                              onChange={async () => {
                                const r = await api.get(`/stations/${selected}`);
                                setStationData(r.data);
                              }}
                            />
                            <div className="flex-1 min-w-0">
                              <div className="truncate text-white flex items-center gap-2">
                                {t.title}
                                {broken && <span className="fl-mono text-[9px] tracking-widest uppercase px-1.5 py-0.5 rounded bg-red-500/20 border border-red-500/40 text-red-300">missing — re-upload</span>}
                              </div>
                              <div className="text-xs text-slate-400 truncate">{t.artist} · {fmtTime(t.duration)} · {t.kind}</div>
                            </div>
                            <button
                              onClick={() => setEditingLyricsTrack(t)}
                              className={`text-slate-500 hover:text-emerald-400 ${(t.lyrics || "").trim() ? "!text-emerald-400" : ""}`}
                              title={(t.lyrics || "").trim() ? "Edit lyrics (saved)" : "Add lyrics"}
                              data-testid={`edit-lyrics-${t.track_id}`}
                            >
                              <FileText className="w-4 h-4" />
                            </button>
                            <button onClick={() => deleteTrack(t.track_id)} className="text-slate-500 hover:text-red-400" data-testid={`delete-track-${t.track_id}`}>
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        );})}
                      </div>
                    )}
                  </div>
                )}

                {/* SHOWS TAB */}
                {tab === "shows" && (
                  <div className="fl-card p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="fl-display text-xl font-bold">Weekly schedule</h3>
                      <button onClick={() => setShowAddShow(true)} className="fl-btn !py-2 !px-3 text-sm" data-testid="add-show-button"><Plus className="w-4 h-4" /> Add show</button>
                    </div>
                    {shows.length === 0 ? (
                      <div className="text-slate-500 fl-mono text-xs uppercase tracking-widest py-12 text-center">// Schedule a talk show or program</div>
                    ) : (
                      <div className="grid sm:grid-cols-2 gap-3">
                        {shows.map(sh => (
                          <div key={sh.show_id} className="border border-white/5 rounded-lg p-4 flex gap-3 items-start">
                            <div className="text-center shrink-0">
                              <div className="fl-mono text-[10px] tracking-widest uppercase text-emerald-500">{DAYS[sh.day_of_week]}</div>
                              <div className="fl-display text-xl font-bold leading-none mt-1">{String(sh.start_hour).padStart(2,"0")}:00</div>
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="font-medium truncate">{sh.title}</div>
                              <div className="text-xs text-slate-400">Host: {sh.host} · {sh.duration_min}m</div>
                              {sh.description && <div className="text-xs text-slate-500 mt-1 line-clamp-2">{sh.description}</div>}
                            </div>
                            <button onClick={() => deleteShow(sh.show_id)} className="text-slate-500 hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* AI DJ */}
                {tab === "ai-dj" && <AiDjPanel station={s} onSave={saveSettings} />}

                {/* SETTINGS */}
                {tab === "settings" && (
                  <SettingsForm station={s} catalog={catalog} onSave={saveSettings} userPlan={user?.plan} />
                )}

                {/* EMBED */}
                {tab === "embed" && <EmbedPanel station={s} />}

                {/* ANALYTICS */}
                {tab === "analytics" && (
                  <div className="space-y-6">
                    <div className="fl-card p-6 space-y-4">
                      <h3 className="fl-display text-xl font-bold">Live analytics</h3>
                      <div className="grid sm:grid-cols-3 gap-3">
                        <Stat label="Listeners now" value={analytics?.current_listeners ?? 0} big />
                        <Stat label="Tracks" value={analytics?.total_tracks ?? 0} big />
                        <Stat label="Shows" value={analytics?.total_shows ?? 0} big />
                      </div>
                      <div className="border-t border-white/5 pt-4 text-sm text-slate-400">
                        <div className="fl-display text-base font-bold text-white mb-1">Grow & profit</div>
                        <ul className="list-disc list-inside space-y-1">
                          <li>Add your <span className="text-emerald-500">Tip Jar URL</span> (PayPal.me / Cash App $tag) in Settings — 100% goes to you, Fliboe doesn&apos;t touch it. <span className="fl-mono text-[10px] uppercase tracking-widest text-slate-500">Premium &amp; Broadcaster plans only.</span></li>
                          <li>Share your <span className="text-emerald-500">unique referral link</span> — every friend who joins via your code is tracked in your Studio.</li>
                          <li>Promote your station with shareable links: <code className="fl-mono text-xs text-emerald-500">/s/{s.station_id}</code></li>
                          <li>Embed your player on your own site (one-line script in the Embed tab).</li>
                        </ul>
                      </div>
                    </div>
                    <DemographicsPanel stationId={s.station_id} />
                  </div>
                )}
              </>
            )}
          </section>
        </div>
      )}

      {/* ACCOUNT panel — export / delete */}
      <AccountPanel />

      {/* AGGGGGGREGATE: Add-by-URL modal */}
      {showAddByUrl && (
        <AddByUrlModal
          stationId={selected}
          onClose={() => setShowAddByUrl(false)}
          onDone={async () => { setShowAddByUrl(false); await reloadStation(); }}
        />
      )}

      {/* LYRICS MODAL — owner edits lyrics for a track */}
      {editingLyricsTrack && (
        <Modal onClose={() => setEditingLyricsTrack(null)} title={`Lyrics · ${editingLyricsTrack.title}`}>
          <div className="space-y-3">
            <div className="text-xs text-slate-400">{editingLyricsTrack.artist} · {fmtTime(editingLyricsTrack.duration)}</div>
            <textarea
              className="fl-input !h-80 !font-mono !text-xs leading-relaxed"
              placeholder="Paste or type the lyrics here. Listeners will see this in the player's Lyrics tab. Leave blank to clear."
              value={lyricsDraft}
              onChange={e => setLyricsDraft(e.target.value)}
              maxLength={20000}
              data-testid="lyrics-draft-textarea"
            />
            <div className="flex items-center justify-between text-[11px] text-slate-500">
              <span>{lyricsDraft.length} / 20000 chars</span>
              <button
                type="button"
                onClick={transcribeLyrics}
                disabled={transcribing}
                className="fl-btn ghost !py-1 !px-2 text-[11px] inline-flex items-center gap-1 disabled:opacity-50"
                data-testid="lyrics-transcribe"
                title="Run OpenAI Whisper on the audio and auto-fill lyrics (~$0.006/min)"
              >
                <Sparkles className="w-3 h-3" />
                {transcribing ? "Transcribing…" : "Transcribe with AI"}
              </button>
            </div>
            <div className="flex gap-2">
              <button onClick={() => setEditingLyricsTrack(null)} className="fl-btn ghost flex-1" data-testid="lyrics-cancel">Cancel</button>
              <button onClick={saveLyrics} disabled={savingLyrics} className="fl-btn flex-1" data-testid="lyrics-save">
                {savingLyrics ? "Saving…" : "Save lyrics"}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* CREATE MODAL */}
      {showCreate && (
        <Modal onClose={() => setShowCreate(false)} title="New station">
          <div className="space-y-3">
            <Field label="Name"><input className="fl-input" value={newSt.name} onChange={e => setNewSt({ ...newSt, name: e.target.value })} placeholder="Midnight Fliboe" data-testid="new-station-name" /></Field>
            <Field label="Tagline"><input className="fl-input" value={newSt.tagline} onChange={e => setNewSt({ ...newSt, tagline: e.target.value })} placeholder="Late-night vibes from..." /></Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Genre">
                <select className="fl-input" value={newSt.genre} onChange={e => setNewSt({ ...newSt, genre: e.target.value })}>
                  {catalog.genres.map(g => <option key={g} value={g}>{g}</option>)}
                </select>
              </Field>
              <Field label="Country">
                <select className="fl-input" value={newSt.country} onChange={e => setNewSt({ ...newSt, country: e.target.value })}>
                  {catalog.countries.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </Field>
              <Field label="Language">
                <select className="fl-input" value={newSt.language} onChange={e => setNewSt({ ...newSt, language: e.target.value })}>
                  {catalog.languages.map(l => <option key={l} value={l}>{l}</option>)}
                </select>
              </Field>
              <Field label="Audience age">
                <select className="fl-input" value={newSt.age_target} onChange={e => setNewSt({ ...newSt, age_target: e.target.value })}>
                  {catalog.age_targets.map(a => <option key={a} value={a}>{a}</option>)}
                </select>
              </Field>
            </div>
            <CoverPicker value={newSt.cover_url} onChange={(url) => setNewSt({ ...newSt, cover_url: url })} />
            <Field label="Tip Jar URL (PayPal.me, Cash App $tag — only used on paid plans)"><input className="fl-input" value={newSt.tip_jar_url} onChange={e => setNewSt({ ...newSt, tip_jar_url: e.target.value })} placeholder="https://paypal.me/yourhandle" /></Field>
            <Field label="Zip / Postal code (so your AI DJ reads YOUR local weather, not the country capital's)">
              <input className="fl-input" value={newSt.zip_code} onChange={e => setNewSt({ ...newSt, zip_code: e.target.value })} placeholder="e.g. 35203 for Birmingham AL" data-testid="station-zip-code" />
            </Field>
            <button onClick={createStation} className="fl-btn w-full mt-3" data-testid="confirm-create-station">Launch station</button>
          </div>
        </Modal>
      )}

      {/* ADD SHOW MODAL */}
      {showAddShow && (
        <Modal onClose={() => setShowAddShow(false)} title="Schedule a show">
          <div className="space-y-3">
            <Field label="Title"><input className="fl-input" value={newShow.title} onChange={e => setNewShow({ ...newShow, title: e.target.value })} data-testid="show-title-input" /></Field>
            <Field label="Host"><input className="fl-input" value={newShow.host} onChange={e => setNewShow({ ...newShow, host: e.target.value })} data-testid="show-host-input" /></Field>
            <div className="grid grid-cols-3 gap-3">
              <Field label="Day">
                <select className="fl-input" value={newShow.day_of_week} onChange={e => setNewShow({ ...newShow, day_of_week: parseInt(e.target.value) })}>
                  {DAYS.map((d, i) => <option key={d} value={i}>{d}</option>)}
                </select>
              </Field>
              <Field label="Hour (0-23)"><input type="number" min={0} max={23} className="fl-input" value={newShow.start_hour} onChange={e => setNewShow({ ...newShow, start_hour: parseInt(e.target.value) || 0 })} /></Field>
              <Field label="Duration (min)"><input type="number" min={15} max={300} className="fl-input" value={newShow.duration_min} onChange={e => setNewShow({ ...newShow, duration_min: parseInt(e.target.value) || 60 })} /></Field>
            </div>
            <Field label="Description"><textarea rows={3} className="fl-input" value={newShow.description} onChange={e => setNewShow({ ...newShow, description: e.target.value })} /></Field>
            <button onClick={addShow} className="fl-btn w-full mt-2" data-testid="confirm-add-show">Add to schedule</button>
          </div>
        </Modal>
      )}
    </main>
  );
}

function SettingsForm({ station, catalog, onSave, userPlan }) {
  const [f, setF] = useState({ ...station });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [coverBusy, setCoverBusy] = useState(false);
  const coverRef = useRef(null);
  useEffect(() => { setF({ ...station }); }, [station.station_id]);
  const tipJarLocked = !userPlan || userPlan === "free";

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const resolveImg = (u) => !u ? "" : (u.startsWith("http") ? u : `${BACKEND_URL}${u}`);

  const handleSave = async () => {
    setSaving(true); setSaved(false);
    try {
      await onSave(f);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } finally {
      setSaving(false);
    }
  };

  const handleCoverUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setCoverBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/images/upload", fd, { headers: { ...authHeaders(), "Content-Type": "multipart/form-data" } });
      setF((prev) => ({ ...prev, cover_url: data.url }));
    } catch (err) {
      alert("Couldn't upload that image. Make sure it's a JPG/PNG/WEBP under 8 MB.");
    } finally {
      setCoverBusy(false);
      if (coverRef.current) coverRef.current.value = "";
    }
  };

  return (
    <div className="fl-card p-6 space-y-3">
      <h3 className="fl-display text-xl font-bold mb-2">Station settings</h3>
      <p className="text-xs text-slate-400 -mt-1">Name and brand this station however you want — your listeners see your brand, not Fliboe's.</p>
      <Field label="Name (your station, your brand)"><input className="fl-input" value={f.name || ""} onChange={e => setF({ ...f, name: e.target.value })} data-testid="settings-name-input" /></Field>
      <Field label="Tagline"><input className="fl-input" value={f.tagline || ""} onChange={e => setF({ ...f, tagline: e.target.value })} /></Field>
      <div className="grid sm:grid-cols-2 gap-3">
        <Field label="Genre"><select className="fl-input" value={f.genre} onChange={e => setF({ ...f, genre: e.target.value })}>{catalog.genres.map(g => <option key={g}>{g}</option>)}</select></Field>
        <Field label="Country"><select className="fl-input" value={f.country} onChange={e => setF({ ...f, country: e.target.value })}>{catalog.countries.map(c => <option key={c}>{c}</option>)}</select></Field>
        <Field label="Language"><select className="fl-input" value={f.language} onChange={e => setF({ ...f, language: e.target.value })}>{catalog.languages.map(l => <option key={l}>{l}</option>)}</select></Field>
        <Field label="Audience age"><select className="fl-input" value={f.age_target} onChange={e => setF({ ...f, age_target: e.target.value })}>{catalog.age_targets.map(a => <option key={a}>{a}</option>)}</select></Field>
      </div>

      {/* Cover image upload */}
      <Field label="Cover image">
        <div className="flex items-center gap-4">
          <div className="w-24 h-24 rounded-lg bg-zinc-900 border border-white/10 overflow-hidden shrink-0 flex items-center justify-center">
            {f.cover_url ? (
              <img src={resolveImg(f.cover_url)} alt="cover" className="w-full h-full object-cover object-top" />
            ) : (
              <span className="fl-mono text-[9px] tracking-widest uppercase text-slate-600 text-center px-2">No cover</span>
            )}
          </div>
          <div className="flex-1 space-y-2">
            <input ref={coverRef} type="file" accept="image/*,.jpg,.jpeg,.png,.webp,.gif" className="hidden" onChange={handleCoverUpload} data-testid="cover-upload-input" />
            <button type="button" onClick={() => coverRef.current?.click()} disabled={coverBusy} className="fl-btn ghost !py-2 !px-3 text-sm" data-testid="cover-upload-button">
              <Upload className="w-4 h-4" /> {coverBusy ? "Uploading…" : (f.cover_url ? "Replace image" : "Upload image")}
            </button>
            {f.cover_url && (
              <button type="button" onClick={() => setF({ ...f, cover_url: "" })} className="block text-xs text-slate-500 hover:text-red-400">Remove cover</button>
            )}
            <p className="text-[11px] text-slate-500">JPG, PNG, WEBP — up to 8 MB. Square works best.</p>
          </div>
        </div>
      </Field>

      <div>
        <Field label={`Tip Jar — PayPal.me, Cash App $tag, Venmo${tipJarLocked ? " (locked: upgrade to Premium or Broadcaster)" : ""}`}>
          <input className="fl-input" value={f.tip_jar_url || ""} onChange={e => setF({ ...f, tip_jar_url: e.target.value })} placeholder="https://paypal.me/yourhandle  ·  $yourCashAppTag  ·  venmo.com/yourhandle" disabled={tipJarLocked} data-testid="settings-tipjar-input" />
        </Field>
        <p className="text-xs text-slate-500 mt-1">
          Paste a full link (best) OR just your handle — e.g. <code className="text-emerald-400">flikkrec</code> auto-becomes <code className="text-emerald-400">paypal.me/flikkrec</code>, <code className="text-emerald-400">$flikkrec</code> becomes <code className="text-emerald-400">cash.app/$flikkrec</code>. 100% of tips go directly to you. Fliboe doesn't process or take a cut.{tipJarLocked && <> <Link to="/pricing" className="text-emerald-500 hover:underline">Upgrade your plan →</Link></>}
        </p>
      </div>

      <div className="flex items-center gap-3 pt-2 pb-24 sm:pb-2">
        <button onClick={handleSave} disabled={saving} className="fl-btn" data-testid="save-settings-button">
          {saving ? <><span className="eq-bar" style={{height:8}}/><span className="eq-bar" style={{height:8}}/> Saving</> : <><Save className="w-4 h-4" /> Save changes</>}
        </button>
        {saved && <span className="fl-mono text-[11px] tracking-widest uppercase text-emerald-400" data-testid="save-success">✓ Saved</span>}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="fl-mono text-[10px] tracking-[0.25em] uppercase text-slate-500">{label}</span>
      <div className="mt-1.5">{children}</div>
    </label>
  );
}

function Stat({ label, value, big }) {
  return (
    <div>
      <div className="fl-mono text-[10px] tracking-[0.25em] uppercase text-slate-500">{label}</div>
      <div className={`fl-display font-extrabold ${big ? "text-4xl" : "text-2xl"} text-emerald-500`}>{value}</div>
    </div>
  );
}

function Modal({ title, children, onClose }) {
  return (
    <div className="fixed inset-0 z-[60] flex items-start sm:items-center justify-center p-4 bg-black/80 backdrop-blur-sm overflow-y-auto" onClick={onClose}>
      <div className="fl-card w-full max-w-md p-6 relative my-6 max-h-[calc(100vh-3rem)] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-3 right-3 text-slate-400 hover:text-white z-10 bg-black/40 rounded-full w-8 h-8 flex items-center justify-center" data-testid="modal-close"><X className="w-4 h-4" /></button>
        <h3 className="fl-display text-2xl font-bold tracking-tight mb-4">{title}</h3>
        {children}
      </div>
    </div>
  );
}

function fmtTime(s) {
  const sec = Math.round(s || 0);
  return `${Math.floor(sec/60)}:${String(sec%60).padStart(2,"0")}`;
}

function CoverPicker({ value, onChange }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const resolveImg = (u) => !u ? "" : (u.startsWith("http") ? u : `${BACKEND_URL}${u}`);

  const handle = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/images/upload", fd, { headers: { ...authHeaders(), "Content-Type": "multipart/form-data" } });
      onChange(data.url);
    } catch (err) {
      alert("Couldn't upload that image. JPG/PNG/WEBP up to 8 MB.");
    } finally {
      setBusy(false);
      if (ref.current) ref.current.value = "";
    }
  };

  return (
    <Field label="Cover image">
      <div className="flex items-center gap-3">
        <div className="w-20 h-20 rounded-lg bg-zinc-900 border border-white/10 overflow-hidden shrink-0 flex items-center justify-center">
          {value ? (
            <img src={resolveImg(value)} alt="cover" className="w-full h-full object-cover object-top" />
          ) : (
            <span className="fl-mono text-[9px] tracking-widest uppercase text-slate-600 text-center px-2">No cover</span>
          )}
        </div>
        <div className="flex-1">
          <input ref={ref} type="file" accept="image/*,.jpg,.jpeg,.png,.webp,.gif" className="hidden" onChange={handle} data-testid="new-station-cover-input" />
          <button type="button" onClick={() => ref.current?.click()} disabled={busy} className="fl-btn ghost !py-2 !px-3 text-sm" data-testid="new-station-cover-button">
            <Upload className="w-4 h-4" /> {busy ? "Uploading…" : (value ? "Replace" : "Upload cover")}
          </button>
          {value && <button type="button" onClick={() => onChange("")} className="ml-2 text-xs text-slate-500 hover:text-red-400">Remove</button>}
          <p className="text-[11px] text-slate-500 mt-1.5">JPG / PNG / WEBP — up to 8 MB.</p>
        </div>
      </div>
    </Field>
  );
}

function AddByUrlModal({ stationId, onClose, onDone }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState([]);

  const submit = async () => {
    const lines = text.split("\n").map(l => l.trim()).filter(Boolean);
    if (!lines.length) return;
    setBusy(true);
    const out = [];
    for (const line of lines) {
      // Optional pipe-format: "URL | Title | Artist"
      const parts = line.split("|").map(p => p.trim());
      const audio_url = parts[0];
      const title = parts[1] || undefined;
      const artist = parts[2] || undefined;
      try {
        const { data } = await api.post(`/stations/${stationId}/tracks-from-url`, { audio_url, title, artist }, { headers: authHeaders() });
        out.push({ ok: true, url: audio_url, title: data.title, duration: data.duration });
      } catch (err) {
        out.push({ ok: false, url: audio_url, error: err?.response?.data?.detail || err?.message || "Failed" });
      }
      setResults([...out]);
    }
    setBusy(false);
  };

  return (
    <Modal onClose={onClose} title="Add tracks by URL">
      <p className="text-xs text-slate-400 mb-3">
        Paste one direct audio URL per line (.mp3 / .wav / .m4a). Duration is auto-detected. Optional format per line:&nbsp;
        <code className="fl-mono text-emerald-500">URL | Title | Artist</code>
      </p>
      <textarea
        rows={6}
        className="fl-input fl-mono text-xs"
        placeholder="https://example.com/song-1.mp3
https://example.com/song-2.mp3 | My Brand New Truck | Country Vibes"
        value={text}
        onChange={(e) => setText(e.target.value)}
        data-testid="add-by-url-textarea"
        disabled={busy}
      />
      <div className="mt-3 flex justify-end gap-2">
        <button onClick={onClose} className="fl-btn ghost !py-2 !px-3 text-sm">Close</button>
        <button onClick={submit} disabled={busy || !text.trim()} className="fl-btn !py-2 !px-3 text-sm" data-testid="add-by-url-submit">
          {busy ? "Adding…" : "Add tracks"}
        </button>
      </div>

      {results.length > 0 && (
        <div className="mt-4 border-t border-white/5 pt-3 space-y-1.5 max-h-48 overflow-y-auto" data-testid="add-by-url-results">
          {results.map((r, i) => (
            <div key={i} className={`text-xs flex items-start gap-2 ${r.ok ? "text-emerald-400" : "text-red-400"}`}>
              <span className="fl-mono">{r.ok ? "✓" : "✗"}</span>
              <span className="flex-1 break-all">
                {r.ok ? <><strong>{r.title}</strong> ({fmtTime(r.duration)})</> : <>{r.url} — {r.error}</>}
              </span>
            </div>
          ))}
          {!busy && results.some(r => r.ok) && (
            <button onClick={onDone} className="fl-btn w-full mt-3 !py-2 text-sm">Done — refresh playlist</button>
          )}
        </div>
      )}
    </Modal>
  );
}

function EmbedPanel({ station }) {
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const scriptSnippet = `<script src="${origin}/api/embed.js" data-fliboe-station="${station.station_id}" async></script>`;
  const iframeSnippet = `<iframe src="${origin}/embed/${station.station_id}" width="340" height="134" style="border:0;border-radius:12px;background:#050505" allow="autoplay" title="Fliboe player"></iframe>`;
  const linkSnippet = `${origin}/s/${station.station_id}`;
  const [copied, setCopied] = useState("");

  const copy = async (key, text) => {
    try { await navigator.clipboard.writeText(text); setCopied(key); setTimeout(() => setCopied(""), 1500); } catch (_) {}
  };

  return (
    <div className="fl-card p-6 space-y-6">
      <div>
        <h3 className="fl-display text-xl font-bold flex items-center gap-2"><Code2 className="w-5 h-5 text-emerald-500" /> Embed anywhere</h3>
        <p className="text-slate-400 text-sm mt-1">Drop this on any blog, fan site, or partner page. Every embed counts toward your live listener stats.</p>
      </div>

      {/* One-line script */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <div className="fl-mono text-[10px] tracking-[0.25em] uppercase text-emerald-500">// Recommended · one-line script</div>
          <button onClick={() => copy("script", scriptSnippet)} className="fl-btn ghost !py-1.5 !px-3 text-xs" data-testid="copy-embed-script">
            {copied === "script" ? <><Check className="w-3.5 h-3.5" /> Copied</> : <><Copy className="w-3.5 h-3.5" /> Copy</>}
          </button>
        </div>
        <pre className="bg-black/60 border border-white/10 rounded-lg p-3 text-xs overflow-x-auto fl-mono text-slate-300">{scriptSnippet}</pre>
      </div>

      {/* Iframe fallback */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <div className="fl-mono text-[10px] tracking-[0.25em] uppercase text-slate-500">// Iframe (for sites that disallow scripts)</div>
          <button onClick={() => copy("iframe", iframeSnippet)} className="fl-btn ghost !py-1.5 !px-3 text-xs" data-testid="copy-embed-iframe">
            {copied === "iframe" ? <><Check className="w-3.5 h-3.5" /> Copied</> : <><Copy className="w-3.5 h-3.5" /> Copy</>}
          </button>
        </div>
        <pre className="bg-black/60 border border-white/10 rounded-lg p-3 text-xs overflow-x-auto fl-mono text-slate-300">{iframeSnippet}</pre>
      </div>

      {/* Shareable link */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <div className="fl-mono text-[10px] tracking-[0.25em] uppercase text-slate-500">// Shareable link</div>
          <button onClick={() => copy("link", linkSnippet)} className="fl-btn ghost !py-1.5 !px-3 text-xs" data-testid="copy-embed-link">
            {copied === "link" ? <><Check className="w-3.5 h-3.5" /> Copied</> : <><Copy className="w-3.5 h-3.5" /> Copy</>}
          </button>
        </div>
        <pre className="bg-black/60 border border-white/10 rounded-lg p-3 text-xs overflow-x-auto fl-mono text-slate-300">{linkSnippet}</pre>
      </div>

      {/* Live preview */}
      <div>
        <div className="fl-mono text-[10px] tracking-[0.25em] uppercase text-slate-500 mb-2">// Live preview</div>
        <div className="bg-zinc-950 p-4 rounded-lg border border-white/5 flex items-center justify-center">
          <iframe
            title="Fliboe embed preview"
            src={`${origin}/embed/${station.station_id}?preview=1`}
            width="340" height="110"
            style={{ border: 0, borderRadius: 12, background: "#050505" }}
            allow="autoplay"
            data-testid="embed-preview-iframe"
          />
        </div>
      </div>

      <div className="border-t border-white/5 pt-4 text-xs text-slate-400">
        Growth tip: ask 5 fans to embed the script on their blog or band page. Each embed becomes a permanent listener funnel — at zero cost.
      </div>
    </div>
  );
}


function AiDjPanel({ station, onSave }) {
  const [stats, setStats] = useState(null);
  const [enabled, setEnabled] = useState(station.dj_enabled !== false);
  const [personality, setPersonality] = useState(station.dj_custom_personality || "");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  const loadStats = async () => {
    try {
      const { data } = await api.get(`/stations/${station.station_id}/dj/stats`, { headers: authHeaders() });
      setStats(data);
    } catch (_) {}
  };
  useEffect(() => { loadStats(); }, [station.station_id]);

  const savePrefs = async () => {
    setBusy(true); setMsg("");
    try {
      await onSave({ dj_enabled: enabled, dj_custom_personality: personality });
      setMsg("Saved.");
      await loadStats();
    } catch (_) { setMsg("Could not save."); }
    setBusy(false);
    setTimeout(() => setMsg(""), 2500);
  };

  const generate = async () => {
    setBusy(true); setMsg("Generating drops — this can take 20–60 seconds for a full rotation…");
    try {
      const { data } = await api.post(`/stations/${station.station_id}/dj/refresh`, {}, { headers: authHeaders() });
      setMsg(`Done. ${data.total_drops} drops cached.`);
      await loadStats();
    } catch (e) {
      setMsg("Generation failed. Check your Universal Key balance and try again.");
    }
    setBusy(false);
  };

  const clearAll = async () => {
    if (!window.confirm("Wipe all cached DJ drops? New ones will be generated automatically.")) return;
    setBusy(true);
    try {
      await api.delete(`/stations/${station.station_id}/dj/drops`, { headers: authHeaders() });
      setMsg("Cleared.");
      await loadStats();
    } catch (_) {}
    setBusy(false);
  };

  return (
    <div className="fl-card p-6 space-y-5" data-testid="ai-dj-panel">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h3 className="fl-display text-xl font-bold flex items-center gap-2">
            <Mic2 className="w-5 h-5 text-emerald-500" /> AI DJ between songs
          </h3>
          <p className="text-sm text-slate-400 mt-1 max-w-xl">
            Your station gets a real-radio feel: a friendly host intros every song and weaves
            in the time of day. Drops are cached, so all worldwide listeners hear the same
            voice at the same second.
          </p>
        </div>
        <label className="flex items-center gap-3 cursor-pointer select-none" data-testid="ai-dj-toggle">
          <input type="checkbox" className="sr-only peer" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
          <span className="relative w-11 h-6 bg-zinc-800 rounded-full transition peer-checked:bg-emerald-500/80 after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:w-5 after:h-5 after:bg-white after:rounded-full after:transition peer-checked:after:translate-x-5" />
          <span className="fl-mono text-xs tracking-widest uppercase">{enabled ? "On" : "Off"}</span>
        </label>
      </div>

      <div>
        <label className="fl-mono text-[10px] tracking-[0.25em] uppercase text-slate-500">Host personality (optional)</label>
        <textarea
          rows={2}
          value={personality}
          onChange={(e) => setPersonality(e.target.value)}
          placeholder='e.g. "warm late-night jazz host with dry wit", "country drawl, mentions Texas often"'
          className="fl-input mt-1 text-sm w-full"
          maxLength={200}
          data-testid="ai-dj-personality"
        />
        <div className="flex items-center justify-between mt-1">
          <span className="fl-mono text-[10px] tracking-widest uppercase text-slate-600">// Voices auto-switch with time of day (morning · day · evening · late-night)</span>
          <span className="fl-mono text-[10px] text-slate-500">{personality.length}/200</span>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        <button onClick={savePrefs} disabled={busy} className="fl-btn !py-2 !px-4 text-sm" data-testid="ai-dj-save">
          <Save className="w-4 h-4" /> Save
        </button>
        <button onClick={generate} disabled={busy} className="fl-btn ghost !py-2 !px-4 text-sm" data-testid="ai-dj-generate">
          <Sparkles className="w-4 h-4 text-emerald-500" /> {busy ? "Working…" : "Generate / refresh drops now"}
        </button>
        <button onClick={clearAll} disabled={busy} className="fl-btn ghost !py-2 !px-4 text-sm" data-testid="ai-dj-clear" title="Useful after editing personality">
          <RefreshCw className="w-4 h-4" /> Clear cache
        </button>
        {msg && <span className="text-xs text-emerald-400 self-center ml-2">{msg}</span>}
      </div>

      <div className="grid sm:grid-cols-3 gap-3 pt-2 border-t border-white/5">
        <Stat label="Drops cached" value={stats?.total_drops_cached ?? 0} />
        <Stat
          label={stats?.this_month?.cap !== null && stats?.this_month?.cap !== undefined
            ? `Drops this month (${stats.this_month.cap} cap)` : "Drops this month"}
          value={stats?.this_month?.drops_generated ?? 0}
        />
        <Stat label="Voice minutes used" value={stats?.this_month?.minutes_total?.toFixed?.(1) ?? "0.0"} />
      </div>
      {stats?.this_month?.cap !== null && stats?.this_month?.cap !== undefined && stats?.this_month?.remaining !== undefined && (
        <div className="text-xs text-slate-500 fl-mono tracking-widest uppercase" data-testid="ai-dj-cap-info">
          // {stats.this_month.remaining} drops remaining on your {stats.this_month.plan} plan this month · resets on the 1st
        </div>
      )}

      <div className="text-xs text-slate-500 fl-mono tracking-widest uppercase">
        // Voices · 5–11 UTC shimmer (morning) · 11–17 nova (daytime) · 17–22 coral (evening) · 22–05 onyx (late-night)
      </div>
    </div>
  );
}

function AccountPanel() {
  const { logout } = useAuth();
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  const exportData = async () => {
    setBusy(true); setMsg("");
    try {
      const { data } = await api.get("/users/me/export", { headers: authHeaders() });
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `fliboe-export-${(data.user?.user_id || "me")}-${Date.now()}.json`;
      a.click();
      URL.revokeObjectURL(url);
      setMsg("Downloaded.");
    } catch (_) { setMsg("Export failed."); }
    setBusy(false);
    setTimeout(() => setMsg(""), 2500);
  };

  const deleteAccount = async () => {
    const ok = window.prompt('Type "DELETE" to permanently remove your account, all your stations, tracks, and uploaded files. This cannot be undone.');
    if (ok !== "DELETE") return;
    setBusy(true);
    try {
      await api.delete("/users/me", { headers: authHeaders() });
      logout();
      window.location.href = "/";
    } catch (_) { setMsg("Delete failed."); setBusy(false); }
  };

  return (
    <section className="mt-12 fl-card p-6" data-testid="account-panel">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="fl-mono text-[10px] tracking-[0.3em] uppercase text-emerald-500">// Account</div>
          <h3 className="fl-display text-xl font-bold mt-1">Your data, your choice</h3>
          <p className="text-xs text-slate-400 mt-1">Export everything we have on you, or permanently delete your account.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button onClick={exportData} disabled={busy} className="fl-btn ghost !py-2 !px-3 text-sm" data-testid="account-export">
            Export my data
          </button>
          <button onClick={deleteAccount} disabled={busy} className="fl-btn !py-2 !px-3 text-sm bg-red-500 hover:bg-red-400" data-testid="account-delete">
            Delete account
          </button>
        </div>
      </div>
      {msg && <div className="mt-3 text-xs text-emerald-400">{msg}</div>}
    </section>
  );
}

